import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useBulkMeterUploadFlow } from "@components/form/meter/bulk_upload/useBulkMeterUploadFlow";
import type { Level1WRSRef } from "./bulkMeterUploadReducer";
import {
  type ManualIntervention,
  type MeterUploadRow,
  type MeterUploadRowData,
  type TrialRunDisplayRow,
  type TrialRunServerRow,
} from "@utils/bulkMeterUpload/buildTrialRunRow";
import type { MeterUploadStatusValue } from "@utils/bulkMeterUpload/computeParentStatus";
import type { LedgerSummary } from "@utils/bulkMeterUpload/ledgerSummary";
import type { NoteOutcome } from "@services/bulkMeterUpload";
import { DraftNote, EMPTY_DRAFT_NOTE } from "@components/notes/NoteComposer";

type ContextValue = {
  level1WRS: Level1WRSRef | undefined;
  setLevel1WRS: (value: any) => void;
  setRowsFromFile: (rows: MeterUploadRow[], fileName: string) => void;
  rows: MeterUploadRow[];
  meterUploadId: string | undefined;
  workflowInstanceId: string | undefined;
  fileName: string | undefined;
  parentStatus: MeterUploadStatusValue;
  displayRows: TrialRunDisplayRow[];
  trialServerRows: Record<string, TrialRunServerRow>;
  isValidFile: boolean;
  setIsValidFile: (value: boolean) => void;
  handleCancel: () => void;
  reset: () => void;
  isComplete: boolean;
  setIsComplete: React.Dispatch<React.SetStateAction<boolean>>;
  isLedgering: boolean;
  isTrialRunning: boolean;
  runTrialRun: () => Promise<void>;
  applyManualIntervention: (
    rowId: string,
    intervention: ManualIntervention,
  ) => Promise<void>;
  saveDraft: () => Promise<string | undefined>;
  hydrateFromServer: () => Promise<void>;
  addRow: (row: MeterUploadRow) => Promise<void>;
  editRow: (rowId: string, patch: MeterUploadRowData) => Promise<void>;
  deleteRow: (rowId: string) => Promise<void>;
  ledger: (
    idArg?: string,
  ) => Promise<
    { noteOutcome: NoteOutcome; summary: LedgerSummary } | undefined
  >;
  draftNote: DraftNote;
  setDraftNote: React.Dispatch<React.SetStateAction<DraftNote>>;
  // True when the page is opened to inspect a finalised upload (?mode=view): the
  // whole flow renders read-only — no add/edit/delete/save/ledger affordances.
  readOnly: boolean;
};

const BulkMeterUploadContext = React.createContext<ContextValue | undefined>(
  undefined,
);

const BulkMeterUploadProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { id: level1ResourceId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const draftId = searchParams.get("draftId") ?? undefined;
  const readOnly = searchParams.get("mode") === "view";

  const flow = useBulkMeterUploadFlow({ level1ResourceId, draftId });
  const [isValidFile, setIsValidFile] = React.useState(true);
  const [isComplete, setIsComplete] = React.useState(false);
  const [draftNote, setDraftNote] =
    React.useState<DraftNote>(EMPTY_DRAFT_NOTE);

  const flowSetRowsFromFile = flow.setRowsFromFile;

  const flowSetLevel1WRS = flow.setLevel1WRS;
  const setLevel1WRS = React.useCallback(
    (value: any) => {
      if (value && typeof value === "object" && "id" in value) {
        flowSetLevel1WRS({ id: value.id, name: value.name ?? "" });
      }
    },
    [flowSetLevel1WRS],
  );

  const handleCancel = React.useCallback(
    () =>
      !!searchParams.get("newWindow")
        ? window.close()
        : navigate(`/polestar/level1wrs/${level1ResourceId}#5`),
    [searchParams, navigate, level1ResourceId],
  );

  const flowReset = flow.reset;
  const reset = React.useCallback(() => {
    setIsValidFile(true);
    flowReset();
  }, [flowReset]);

  const values: ContextValue = {
    level1WRS: flow.level1WRS,
    setLevel1WRS,
    setRowsFromFile: flowSetRowsFromFile,
    rows: flow.rows,
    meterUploadId: flow.meterUploadId,
    workflowInstanceId: flow.workflowInstanceId,
    fileName: flow.fileName,
    parentStatus: flow.parentStatus,
    displayRows: flow.displayRows,
    trialServerRows: flow.trialServerRows,
    handleCancel,
    isValidFile,
    setIsValidFile,
    reset,
    isComplete,
    setIsComplete,
    isLedgering: flow.isLedgering,
    isTrialRunning: flow.isTrialRunning,
    runTrialRun: flow.runTrialRun,
    applyManualIntervention: flow.applyManualIntervention,
    saveDraft: flow.saveDraft,
    hydrateFromServer: flow.hydrateFromServer,
    addRow: flow.addRow,
    editRow: flow.editRow,
    deleteRow: flow.deleteRow,
    ledger: flow.ledger,
    draftNote,
    setDraftNote,
    readOnly,
  };

  return (
    <BulkMeterUploadContext.Provider value={values}>
      {children}
    </BulkMeterUploadContext.Provider>
  );
};

const useBulkMeterUploadContext = () => {
  const context = React.useContext(BulkMeterUploadContext);
  if (context === undefined) {
    throw new Error(
      "useBulkMeterUploadContext must be used within a BulkUploadProvider",
    );
  }
  return context;
};

export { BulkMeterUploadProvider, useBulkMeterUploadContext };
